import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { FaFileImport, FaPlus, FaTrash } from "react-icons/fa";

import "react-toastify/dist/ReactToastify.css";

import Breadcrumb from "../../components/Breadcrumb";
import Button from "../../components/Button/Button";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";

import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";

import usePeseePage from "./hooks/usePeseePage";

import PeseeFilters from "./components/PeseeFilters";
import PeseeHeader from "./components/PeseeHeader";
import PeseeQuotaCard from "./components/PeseeQuotaCard";
import PeseeTable from "./components/PeseeTable";
import PeseeToolbar from "./components/PeseeToolbar";

// À déplacer depuis ton ancienne page vers src/pages/Pesee/modals/
import CreateCompetiteurModal from "./modals/CreateCompetiteurModal";
import EditCompetiteurModal from "./modals/EditCompetiteurModal";
import UploadJudokasModal from "./modals/UploadJudokasModal";

// À déplacer depuis ton ancienne page vers src/pages/Pesee/components/
import UploadDemoJudokasButton from "./components/UploadDemoJudokasButton";

const PeseePage = () => {
  useScrollToTop();

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const userRole = getRoleFromRoleId(user?.role_id);
  const isDemo = Number(user?.role_id) === 2;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [competiteurToEdit, setCompetiteurToEdit] = useState(null);

  const {
    competition,
    loading,

    disciplinesList,
    filters,
    updateFilter,
    resetFilters,

    options,
    pageRows,
    total,
    page,
    perPage,
    totalPages,
    setPage,
    setPerPage,

    quota,

    actions,
  } = usePeseePage({
    competitionId: id,
    navigate,
  });

  const openEditModal = (competiteur) => {
    setCompetiteurToEdit(competiteur);
    setIsEditModalOpen(true);
  };

  if (loading) {
    return (
      <div className="mt-[140px] flex min-h-[55vh] items-center justify-center px-4 text-center text-slate-600 dark:text-slate-300">
        Chargement des pesées…
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="mt-[140px] flex min-h-[55vh] items-center justify-center px-4 text-center text-slate-600 dark:text-slate-300">
        Erreur de chargement de la compétition.
      </div>
    );
  }

  return (
    <main className="mt-[130px] min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 px-3 pb-10 text-slate-950 dark:bg-slate-950 dark:text-white sm:px-4 lg:px-6">
      <ScrollIndicator />

      <PeseeHeader
        competition={competition}
        userRole={userRole}
        isDemo={isDemo}
      />

      <div className="mx-auto mb-4 w-full max-w-[1680px]">
        <Breadcrumb competitionName={competition?.nom_competition} />
      </div>

      <div className="mx-auto mb-4 grid w-full max-w-[1680px] gap-3 xl:grid-cols-[1fr_360px]">
        <PeseeToolbar
          total={total}
          isDemo={isDemo}
          onExportCsv={actions.onExportCsv}
          exportDisabled={!total}
        >
          {isDemo ? (
            <UploadDemoJudokasButton
              competitionId={id}
              refreshJudokas={actions.fetchJudokas}
            />
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                icon={FaFileImport}
                onClick={() => setIsUploadModalOpen(true)}
                className="rounded-xl"
              >
                Importer
              </Button>

              <Button
                variant="brand"
                size="sm"
                icon={FaPlus}
                onClick={() => setIsCreateModalOpen(true)}
                className="rounded-xl"
              >
                Ajouter
              </Button>

              <Button
                variant="danger"
                size="sm"
                icon={FaTrash}
                onClick={actions.onDeleteFiltered}
                className="rounded-xl"
              >
                Supprimer filtrés
              </Button>
            </>
          )}
        </PeseeToolbar>

        <PeseeQuotaCard quota={quota} />
      </div>

      <div className="mx-auto mb-4 w-full max-w-[1680px]">
        <PeseeFilters
          filters={filters}
          updateFilter={updateFilter}
          resetFilters={resetFilters}
          disciplinesList={disciplinesList}
          options={options}
        />
      </div>

      <div className="mx-auto w-full max-w-[1680px]">
        <PeseeTable
          rows={pageRows}
          page={page}
          perPage={perPage}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onTogglePresence={actions.onTogglePresence}
          onSaveWeight={actions.onSaveWeight}
          onEdit={openEditModal}
          onDelete={actions.onDelete}
        />
      </div>

      <CreateCompetiteurModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        competitionId={id}
        refreshList={actions.fetchJudokas}
      />

      <EditCompetiteurModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        competiteur={competiteurToEdit}
        refreshList={actions.fetchJudokas}
      />

      <UploadJudokasModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        competitionId={id}
        refreshJudokas={actions.fetchJudokas}
      />

      <ToastContainer position="bottom-right" />
    </main>
  );
};

export default PeseePage;